/**
 * i18n Helper for translations
 * Simple implementation without external dependencies
 */

import enTranslations from "./en.json";
import uaTranslations from "./ua.json";
import {
  NavigationTranslations,
  SupportedLanguage,
  TranslationKey,
} from "./types";

const translations: Record<SupportedLanguage, NavigationTranslations> = {
  en: enTranslations,
  ua: uaTranslations,
};

/**
 * Get nested value from object by string path
 * Example: getNestedValue(obj, 'navigation.items.dashboard')
 */
function getNestedValue(obj: any, path: string | null | undefined): string {
  if (!path) return "";
  return path.split(".").reduce((current, key) => current?.[key], obj) || path;
}

/**
 * Get translation by key
 * @param key - Translation key (e.g., 'navigation.items.dashboard')
 * @param language - Language code ('en' | 'ua')
 * @returns Translated string
 */
export function translate(
  key: TranslationKey,
  language: SupportedLanguage = "ua"
): string {
  const translation = translations[language];
  if (!translation) {
    console.warn(`Translation not found for language: ${language}`);
    return key;
  }

  return getNestedValue(translation, key);
}

/**
 * Get translation function for specific language
 * @param language - Language code
 * @returns Translation function
 */
export function getTranslator(language: SupportedLanguage = "ua") {
  return (key: TranslationKey) => translate(key, language);
}

/**
 * Get all translations for a specific language
 * @param language - Language code
 * @returns Full translation object
 */
export function getTranslations(
  language: SupportedLanguage = "ua"
): NavigationTranslations {
  return translations[language];
}

export { enTranslations, uaTranslations };
export type { NavigationTranslations, SupportedLanguage, TranslationKey };
